/**
 * Slack-parity message extras (docs/design/slack-gap.md): the message menu's Copy link / Pin / Mark unread entries,
 * the "Pinned by …" line on a pinned message, and the channel header's Pinned messages button with its list.
 */
import type { LinkUnfurl, Member, Message, SharedMessage } from "@bundle/contracts";
import { Unfurl, type PinsState, type RichTextContext } from "@bundle/core";
import { Avatar, Button, EmbedFrame, Icon, IconButton, Image, Popover, Pressable, ScrollView, Spinner, Stack, Text, openLink, type MenuItem } from "../primitives/index.ts";
import { useFormat, useT } from "../i18n/I18nProvider.tsx";
import { RichText } from "./RichText.tsx";

/** What a message row offers beyond reply / edit / delete (each only when the screen passes it). */
export type MessageExtras = {
  /** "Copy link" (the screen copies the message link: ChannelViewModel.messageLink). */
  onCopyLink?: () => void;
  /** "Pin to this conversation" / "Unpin" (ChannelViewModel.canPin / togglePin). */
  pin?: { pinned: boolean; onToggle: () => void };
  /** "Mark unread" (ChannelViewModel.canMarkUnread / markUnread). */
  onMarkUnread?: () => void;
  /** The message is pinned: who pinned it (shown above the message). */
  pinnedByName?: string;
  /** "Save for later" ↔ "Remove from Later" (LaterViewModel.toggleSaved): a hover action (and so a context-menu entry). */
  later?: { saved: boolean; onToggle: () => void };
  /** "Remind me about this" (opens the RemindSheet). */
  onRemind?: () => void;
  /** "Share message" (opens the share sheet): a hover action (and so a context-menu entry). */
  onShare?: () => void;
  /** A shared message's quote was pressed: open the original in context. */
  onOpenShared?: (shared: SharedMessage) => void;
  /** My message: remove one link's preview (ChannelViewModel.canHideUnfurl / hideUnfurl). */
  onHideUnfurl?: (url: string) => void;
  /** Someone else's message: "Report message" (opens the Report sheet; logic/moderation canReport). */
  onReport?: () => void;
  /** Its author can be blocked: "Block @name" (opens the confirm; logic/moderation canBlock). */
  block?: { name: string; onBlock: () => void };
};

type Translate = ReturnType<typeof useT>;

/** The "…" menu with the extras added before Edit and the destructive entries (Edit, Delete stay last, like Slack). */
export function withMessageExtras(
  more: { label: string; items: MenuItem[] } | undefined,
  extras: MessageExtras | undefined,
  t: Translate,
): { label: string; items: MenuItem[] } | undefined {
  if (extras === undefined) return more;
  const added: MenuItem[] = [];
  // Save for later and Share message are hover-bar buttons (MessageRow), so the right-click menu has them already;
  // like Slack, the "…" menu doesn't repeat them.
  if (extras.onRemind !== undefined) added.push({ id: "remind", label: t("message.remindMe"), icon: "clock", onSelect: extras.onRemind });
  if (extras.onCopyLink !== undefined) added.push({ id: "copy-link", label: t("message.copyLink"), icon: "link", onSelect: extras.onCopyLink });
  if (extras.pin !== undefined) added.push({ id: "pin", label: t(extras.pin.pinned ? "message.unpin" : "message.pin"), icon: "pin", onSelect: extras.pin.onToggle });
  if (extras.onMarkUnread !== undefined) added.push({ id: "mark-unread", label: t("message.markUnread"), icon: "eyeOff", onSelect: extras.onMarkUnread });
  // Report & block (Slack keeps them at the bottom of someone else's message menu).
  const moderation: MenuItem[] = [];
  if (extras.onReport !== undefined) moderation.push({ id: "report", label: t("moderation.report.action"), icon: "alert", onSelect: extras.onReport });
  if (extras.block !== undefined) moderation.push({ id: "block", label: t("moderation.block.action", { name: extras.block.name }), icon: "ban", onSelect: extras.block.onBlock });
  if (added.length === 0 && moderation.length === 0) return more;
  const items = more?.items ?? [];
  // Slack: Edit and Delete close the menu.
  const firstDestructive = items.findIndex((i) => i.destructive === true || i.id === "edit");
  const at = firstDestructive < 0 ? items.length : firstDestructive;
  return { label: more?.label ?? t("message.more"), items: [...items.slice(0, at), ...added, ...items.slice(at), ...moderation] };
}

/** A shared (forwarded) message: the original's author, text and where/when it was posted (Slack's share card). */
export function SharedQuote({ shared, ctx, onOpen, testID }: { shared: SharedMessage; ctx: RichTextContext; onOpen?: () => void; testID: string }) {
  const t = useT();
  const fmt = useFormat();
  const author = ctx.members[shared.authorId];
  const c = ctx.channels[shared.channelId];
  const where = c === undefined ? undefined : c.kind === "dm" || c.kind === "group_dm" ? t("message.shared.dm") : `#${c.name}`;
  return (
    <Stack gap="1" paddingX="3" paddingY="2" radius="md" border="border.subtle" background="bg.sunken" testID={testID}>
      <Stack direction="horizontal" align="center" gap="2">
        <Avatar name={author?.displayName ?? "?"} src={author?.avatarUrl} size="avatar.xs" />
        <Text weight="semibold" lines={1}>
          {author?.displayName ?? t("message.shared.someone")}
        </Text>
      </Stack>
      <RichText blocks={shared.blocks} ctx={ctx} />
      <Stack direction="horizontal" align="center" gap="2" wrap>
        <Text variant="caption" color="text.tertiary">
          {where === undefined ? fmt.dateTime(shared.createdAt) : t("message.shared.postedIn", { where, when: fmt.dateTime(shared.createdAt) })}
        </Text>
        {onOpen === undefined || c === undefined ? null : <Button variant="ghost" size="sm" label={t("message.shared.view")} onPress={onOpen} testID={`${testID}-view`} />}
      </Stack>
    </Stack>
  );
}

/**
 * A link preview under a message (Slack's unfurl): a left rule, the site, the title as a link, the description and
 * the page's image (a play badge on videos). YouTube, Vimeo and Loom links get Slack's inline player instead of the
 * image: the poster with a play button, and the host's privacy-mode player in its place once pressed
 * (`Unfurl.videoEmbedUrl`, `EmbedFrame`). The author gets a remove button on hover. The image comes through the
 * server's image proxy (the site never sees readers).
 */
export function UnfurlCard({ unfurl, onRemove, testID }: { unfurl: LinkUnfurl; onRemove?: () => void; testID: string }) {
  const t = useT();
  const open = () => openLink(unfurl.url);
  const embed = Unfurl.videoEmbedUrl(unfurl);
  const wide = embed !== undefined || (unfurl.imageUrl !== undefined && unfurl.imageWidth !== undefined && unfurl.imageHeight !== undefined && unfurl.imageWidth >= 400 && unfurl.imageWidth >= unfurl.imageHeight * 1.3);
  const name = unfurl.title ?? Unfurl.unfurlSiteLabel(unfurl);
  const image =
    embed !== undefined ? (
      <EmbedFrame src={embed} title={name} playLabel={t("message.unfurl.play", { title: name })} poster={unfurl.imageUrl} width={360} testID={`${testID}-player`} />
    ) : unfurl.imageUrl === undefined ? null : (
      <Pressable onPress={open} radius="sm" accessibilityLabel={unfurl.title ?? unfurl.url} testID={`${testID}-image`}>
        <Image src={unfurl.imageUrl} alt="" width={wide ? 360 : 72} height={wide ? Math.round((360 * unfurl.imageHeight!) / unfurl.imageWidth!) : 72} radius="sm" fit="cover" fallback={null} />
      </Pressable>
    );
  return (
    <Stack direction="horizontal" gap="2" paddingX="3" paddingY="2" radius="md" border="border.subtle" hoverGroup accessibilityLabel={t("message.unfurl.label", { title: unfurl.title ?? Unfurl.unfurlSiteLabel(unfurl) })} testID={testID}>
      <Stack gap="1" grow>
        <Stack direction={wide ? "vertical" : "horizontal"} gap="2">
          <Stack gap="0.5" grow>
            <Stack direction="horizontal" align="center" gap="1">
              {unfurl.video === true ? <Icon name="play" size="icon.sm" color="text.secondary" /> : null}
              <Text variant="footnote" weight="semibold" color="text.secondary" lines={1}>
                {Unfurl.unfurlSiteLabel(unfurl)}
              </Text>
            </Stack>
            {unfurl.title === undefined ? null : (
              <Pressable onPress={open} accessibilityLabel={unfurl.title} testID={`${testID}-title`}>
                <Text weight="semibold" color="text.link" lines={2}>
                  {unfurl.title}
                </Text>
              </Pressable>
            )}
            {unfurl.description === undefined ? null : (
              <Text variant="footnote" lines={3}>
                {unfurl.description}
              </Text>
            )}
          </Stack>
          {image}
        </Stack>
      </Stack>
      {onRemove === undefined ? null : (
        <Stack revealOnHover>
          <IconButton icon="close" size="sm" accessibilityLabel={t("message.unfurl.remove")} onPress={onRemove} testID={`${testID}-remove`} />
        </Stack>
      )}
    </Stack>
  );
}

/** "🔖 Saved for later" above a saved message (Slack). */
export function SavedLine({ testID }: { testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" align="center" gap="1.5" testID={testID}>
      <Icon name="bookmark" size="icon.sm" color="accent.text" />
      <Text variant="footnote" color="accent.text">
        {t("later.saved")}
      </Text>
    </Stack>
  );
}

/** "📌 Pinned by Ada" above a pinned message. */
export function PinnedLine({ name, testID }: { name: string; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" align="center" gap="1.5" testID={testID}>
      <Icon name="pin" size="icon.sm" color="text.secondary" />
      <Text variant="footnote" color="text.secondary">
        {t("message.pinnedBy", { name })}
      </Text>
    </Stack>
  );
}

export type PinnedButtonProps = {
  state: PinsState | undefined;
  onOpen: () => void;
  onClose: () => void;
  /** Opens the message in the conversation (scrolled to it). */
  onJump: (m: Message) => void;
  members: Record<string, Member>;
  ctx: RichTextContext;
  testID: string;
};

/** Channel header button → the conversation's pinned messages, newest pin first; a row opens it in context. */
export function PinnedButton(p: PinnedButtonProps) {
  const t = useT();
  const fmt = useFormat();
  const open = p.state !== undefined;
  return (
    <Popover
      open={open}
      onClose={p.onClose}
      side="bottom"
      align="end"
      width="picker"
      anchor={<IconButton icon="pin" accessibilityLabel={t("channel.pins.open")} onPress={open ? p.onClose : p.onOpen} testID={p.testID} />}
    >
      <Stack gap="2" padding="3" testID={`${p.testID}-list`}>
        <Text weight="semibold">{t("channel.pins.title")}</Text>
        {p.state === undefined ? null : p.state.status === "loading" && p.state.messages.length === 0 ? (
          <Stack align="center" padding="4">
            <Spinner accessibilityLabel={t("common.loading")} />
          </Stack>
        ) : p.state.status === "error" && p.state.messages.length === 0 ? (
          <Stack gap="2">
            <Text variant="footnote" color="text.secondary">
              {t("channel.pins.error")}
            </Text>
            <Button label={t("common.retry")} variant="secondary" size="sm" onPress={p.onOpen} testID={`${p.testID}-retry`} />
          </Stack>
        ) : p.state.messages.length === 0 ? (
          <Text variant="footnote" color="text.secondary" testID={`${p.testID}-empty`}>
            {t("channel.pins.empty")}
          </Text>
        ) : (
          <ScrollView>
            <Stack gap="1">
              {p.state.messages.map((m) => {
                const author = p.members[m.authorId];
                const name = author?.displayName ?? m.authorId;
                return (
                  <Pressable key={m.id} radius="md" onPress={() => p.onJump(m)} accessibilityLabel={`${t("channel.pins.jump")}: ${name}`} testID={`${p.testID}-item-${m.id}`}>
                    <Stack direction="horizontal" align="start" gap="2" padding="2">
                      <Avatar name={name} src={author?.avatarUrl} size="avatar.sm" />
                      <Stack grow gap="0.5">
                        <Stack direction="horizontal" align="center" gap="2">
                          <Text variant="footnote" weight="semibold" lines={1}>
                            {name}
                          </Text>
                          <Text variant="caption" color="text.tertiary">
                            {fmt.time(m.createdAt)}
                          </Text>
                        </Stack>
                        <PinnedPreview message={m} ctx={p.ctx} />
                      </Stack>
                    </Stack>
                  </Pressable>
                );
              })}
            </Stack>
          </ScrollView>
        )}
      </Stack>
    </Popover>
  );
}

function PinnedPreview({ message, ctx }: { message: Message; ctx: RichTextContext }) {
  return (
    <Stack testID={`pinned-preview-${message.id}`}>
      <RichText blocks={message.blocks} ctx={ctx} />
    </Stack>
  );
}
